<template>
  <v-card class="support-card" elevation="1">
    <v-card-text>
      <div class="support-header">
        <span class="support-id">Ticket #{{ support.sup_id }}</span>
        <span class="support-date">
          {{ formatDate(support.sup_created_date) }}
        </span>
      </div>

      <p class="support-title">
        {{ support.sup_title }}
      </p>

      <div class="support-stats" v-if="!isLoadingStats">
        <div class="stat-item">
          <v-icon size="16" color="#666" class="mr-1">mdi-message-text-outline</v-icon>
          <span>{{ talkCountLabel }}</span>
        </div>
        <div class="stat-item">
          <v-icon size="16" color="#666" class="mr-1">mdi-paperclip</v-icon>
          <span>{{ fileCountLabel }}</span>
        </div>
      </div>

      <div class="support-stats" v-else>
        <v-progress-circular indeterminate color="#EA5545" size="18"></v-progress-circular>
        <span class="stats-state-text">Lade Statistiken...</span>
      </div>

      <div class="support-actions">
        <router-link
          :to="{ name: 'support', params: { id: support.sup_id } }"
        >
          <v-btn
            color="#EA5545"
            size="small"
            variant="text"
            prepend-icon="mdi-ticket"
          >
            Ticket öffnen
          </v-btn>
        </router-link>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { inject, ref, computed, onMounted, defineProps } from 'vue'

const props = defineProps({
  support: {
    type: Object,
    required: true
  }
})

const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')

const talkCount = ref(null)
const fileCount = ref(null)
const isLoadingStats = ref(false)

const talkCountLabel = computed(() => {
  if (talkCount.value === null) return 'Nachrichten ...'
  if (talkCount.value === 1) return '1 Nachricht'
  return `${talkCount.value} Nachrichten`
})

const fileCountLabel = computed(() => {
  if (fileCount.value === null) return 'Dateien ...'
  if (fileCount.value === 1) return '1 Datei'
  return `${fileCount.value} Dateien`
})

function formatDate(dateString) {
  if (!dateString || dateString === '1973-01-01 00:00:00') return 'N/A'

  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return dateString

  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

async function loadStats() {
  isLoadingStats.value = true
  try {
    const supId = props.support.sup_id

    // Talks zählen
    const talksResponse = await baseCalls.clearmentPost({
      action: 'getsupporttalks',
      sup_id: supId
    })
    if (talksResponse.data && talksResponse.data.success && Array.isArray(talksResponse.data.talks)) {
      talkCount.value = talksResponse.data.talks.length
    } else {
      talkCount.value = 0
    }

    // Files zählen (über getsupport, dort sind files enthalten)
    const supportResponse = await baseCalls.clearmentPost({
      action: 'getsupport',
      sup_id: supId
    })
    if (
      supportResponse.data &&
      supportResponse.data.success &&
      supportResponse.data.support &&
      Array.isArray(supportResponse.data.support.files)
    ) {
      fileCount.value = supportResponse.data.support.files.length
    } else {
      fileCount.value = 0
    }
  } catch (err) {
    console.error('Error loading support stats:', err)
    if (errorStore && typeof errorStore.setError === 'function') {
      errorStore.setError(true, 'Support-Statistiken konnten nicht geladen werden')
    }
    talkCount.value = 0
    fileCount.value = 0
  } finally {
    isLoadingStats.value = false
  }
}

onMounted(() => {
  loadStats()
})
</script>

<style scoped src="@/assets/styles/sfc/components/objects/ContractSupportItem.css"></style>
